import { Spacer, VDBText, CheckBox } from "@components/atoms";
import React, { useEffect, useState } from "react";
import { Pressable } from "react-native";
import styled from "styled-components/native";
import { Image } from "expo-image";
import { SpaceType } from "@components/atoms/Spacer/types";
import VDBIcon from "@components/icons/VDBIcon";
import { IconsVariant } from "@components/icons/VDBIcon";
import { DefaultTheme, useTheme } from "styled-components/native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { isPair } from "@utils/product/productValidators";
import { VDBIcons } from "@utils/VDBIcons";
import { useTranslation } from "react-i18next";
import {
  getImageSource,
  getIOSImageProps,
  ImageSizePreset,
} from "@utils/image/imageOptimizer";
import { ProductCategory } from "@utils/constants";
import { FALLBACK_IMAGE_ASSET } from "@utils/product/searchResultConstants";
import { Diamond } from "@src/types/DiamondDetail";
import EmptyImagePreview from "@components/molecules/GalleryView/utils/EmptyImage/EmptyImagePreview";
import { getShapeValue } from "@components/molecules/GalleryView/utils/EmptyImage/utils";
import {
  getOptimizedListImageUrl,
  isImageUrlEmptyOrUndefined,
} from "@components/molecules/Utils";
import { useSelector } from "react-redux";
import { useProductInfo } from "@scenes/pdp/utils/utils";
import { getProductIconColor } from "@scenes/favorites/utils/FavoritesUtils";

type ImageComponentJewelryProps = {
  mItem: Diamond;
  addNewConfig?: () => void;
  isChecked?: boolean;
  onCheckChange?: (checked: boolean) => void;
  onDelete?: () => void;
};

const ImageComponentJewelry = ({
  mItem,
  addNewConfig,
  isChecked = false,
  onCheckChange,
  onDelete,
}: ImageComponentJewelryProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const [imageUri, setImageUri] = useState(
    !isImageUrlEmptyOrUndefined(mItem)
      ? getOptimizedListImageUrl(mItem)
      : FALLBACK_IMAGE_ASSET
  );
  const [showImageContainer, setShowImageContainer] = useState(
    !isImageUrlEmptyOrUndefined(mItem)
  );
  const [showErrorImage, setShowErrorImage] = useState(
    isImageUrlEmptyOrUndefined(mItem)
  );
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const productType = mItem?.type ?? ProductCategory.JEWELRY;
  const { productName } = useProductInfo(t, productType, mItem, isCustomApp);

  useEffect(() => {
    const isEmpty = isImageUrlEmptyOrUndefined(mItem);
    setImageUri(
      isEmpty ? FALLBACK_IMAGE_ASSET : getOptimizedListImageUrl(mItem)
    );
    setShowImageContainer(!isEmpty);
    setShowErrorImage(isEmpty);
  }, [mItem?.id, mItem?.image_url, mItem?.image_thumb_url, mItem?.short_title]);

  return (
    <ImageAndButtonContainer>
      <ImageAndContentRow>
        {onCheckChange ? (
          <CheckboxWrap>
            <CheckBox
              fillColor={theme["knob-bor"]}
              checkBoxSize={20}
              isChecked={isChecked}
              checkedFunction={(checked: boolean) => onCheckChange(checked)}
            />
          </CheckboxWrap>
        ) : null}
        <ImageResourceContainer>
          {showErrorImage ? (
            <FallbackImageContainer theme={theme}>
              <EmptyImagePreview
                shape={getShapeValue(mItem)}
                size={30}
                isPair={isPair(mItem) ?? false}
              />
            </FallbackImageContainer>
          ) : showImageContainer ? (
            <Image
              style={{
                height: 72,
                width: 72,
                borderRadius: 8,
                borderColor: theme["core-03"],
                borderWidth: 1,
              }}
              source={getImageSource(
                imageUri || FALLBACK_IMAGE_ASSET,
                ImageSizePreset.LIST_THUMB
              )}
              contentFit="cover"
              {...getIOSImageProps()}
              onError={() => {
                if (mItem?.video_url) {
                  setShowImageContainer(false);
                } else {
                  setShowErrorImage(true);
                  setShowImageContainer(false);
                  setImageUri(FALLBACK_IMAGE_ASSET);
                }
              }}
            />
          ) : (
            <VideoBackgroundContainer theme={theme}>
              <VideoPlayIconContainer theme={theme}>
                <VDBIcon
                  color={theme["core-06"]}
                  size={20}
                  variant={IconsVariant.VDBCustom}
                  name={VDBIcons.PLAY}
                />
              </VideoPlayIconContainer>
            </VideoBackgroundContainer>
          )}
        </ImageResourceContainer>
        <FirstContentContainer theme={theme}>
          {mItem?.short_title && (
            <>
              <VDBText
                type={typography["Minor-M"]}
                color={theme["core-05"]}
                numberOfLines={2}
                ellipsizeMode="tail"
              >
                {mItem?.short_title}
              </VDBText>
              <Spacer size={SpaceType.ExtraSmall} />
            </>
          )}
          {productName ? (
            <>
              <VDBText
                color={theme[getProductIconColor(productType)]}
                type={typography["Minor-M"]}
              >
                {productName}
              </VDBText>
              <Spacer size={SpaceType.ExtraSmall} />
            </>
          ) : null}
          {mItem?.stock_num && (
            <TitleContainer>
              <Spacer size={SpaceType.ExtraSmall} />
              <TextContainer>
                <VDBText
                  type={{ ...typography["Minor-R"], flexShrink: 0 }}
                  color={theme["core-06"]}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {t("searchResult.listView.style.text", "Style #")}:
                </VDBText>
                <Spacer size={SpaceType.ExtraSmall} />
                <VDBText
                  type={{ ...typography["Minor-M"], flex: 1, minWidth: 0 }}
                  color={theme["core-05"]}
                >
                  {mItem?.stock_num}
                </VDBText>
              </TextContainer>
            </TitleContainer>
          )}
          {addNewConfig ? (
            <>
              <Spacer size={SpaceType.ExtraSmall} />
              <AddConfigurationButton
                theme={theme}
                onPress={() => addNewConfig()}
              >
                <VDBText
                  type={typography["Minor-M"]}
                  color={theme["jewelry-06"]}
                >
                  {t(
                    "searchResult.jewelry.addConfiguration.text",
                    "Add Configuration"
                  )}
                </VDBText>
                <Spacer size={SpaceType.Basic} />
                <VDBIcon
                  name={VDBIcons.ADD_CIRCLE}
                  variant={IconsVariant.VDBCustom}
                  size={18}
                  color={theme["jewelry-06"]}
                />
              </AddConfigurationButton>
            </>
          ) : null}
        </FirstContentContainer>
        {onDelete ? (
          <DeleteButton onPress={onDelete} hitSlop={8}>
            <VDBIcon
              color={theme["core-06"]}
              size={24}
              variant={IconsVariant.VDBCustom}
              name={VDBIcons.TRASH}
            />
          </DeleteButton>
        ) : null}
      </ImageAndContentRow>
    </ImageAndButtonContainer>
  );
};

const FirstContentContainer = styled.View<any>`
  flex: 1;
  min-width: 0;
  margin-start: 8px;
  justify-content: flex-start;
  align-items: flex-start;
`;

const ImageAndButtonContainer = styled.View`
  height: auto;
  padding: 16px 16px 8px;
  width: 100%;
`;

const ImageAndContentRow = styled.View`
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
`;

const CheckboxWrap = styled.View`
  height: 72px;
  justify-content: center;
`;

const DeleteButton = styled(Pressable)`
  margin-left: 8px;
  align-self: flex-start;
`;

const ImageResourceContainer = styled.View`
  padding-left: 2px;
  justify-content: center;
`;

const TitleContainer = styled.View<any>`
  width: 100%;
`;

const VideoPlayIconContainer = styled.View<any>`
  height: 28px;
  width: 28px;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: ${({ theme }) => theme["over-02"]};
  border-radius: 14px;
`;

const VideoBackgroundContainer = styled.View<any>`
  height: 48px;
  width: 48px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme["core-01"]};
`;

const FallbackImageContainer = styled.View<any>`
  width: 48px;
  height: 48px;
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: 8px;
  justify-content: center;
  align-items: center;
  border-color: ${({ theme }) => theme["core-03"]};
  border-width: 1px;
`;

const TextContainer = styled.View`
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
`;

const AddConfigurationButton = styled(Pressable)<{ theme: DefaultTheme }>`
  flex-direction: row;
  align-items: center;
`;

export default React.memo(ImageComponentJewelry);
